import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { apiErrorMessage } from "../utils/apiError";
import {
 HiOutlinePlus,
 HiOutlineTrash,
 HiOutlineSearch,
 HiOutlineX,
 HiOutlineChevronLeft,
 HiOutlineAcademicCap,
 HiOutlineExclamation,
 HiOutlineUpload,
 HiOutlineDownload,
 HiOutlineDocumentText,
} from "react-icons/hi";
import {
 fetchMasterData,
 fetchSchools,
 fetchSchoolById,
 fetchBlockById,
 fetchDistrictById,
 createSchool,
 updateSchool,
 deleteSchool,
 bulkActionSchools,
 bulkUpload,
 fetchStudents,
} from "../services/masterDataApi";
import SearchableDropdown, { staticFetchFn } from "../components/SearchableDropdown";
import Toast from "../components/Toast";
import Pagination from "../components/Pagination";
import FilterDropdown from "../components/FilterDropdown";
import { useDebounce } from "../hooks/useDebounce";
import { useUserRole } from "../hooks/useUserRole";

// UDISE code is a purely-digit identifier — a leading "-" is a distinct,
// more specific mistake than "contains a letter", so it gets its own message.
function getUdiseCodeError(raw) {
 const trimmed = raw.trim();
 if (!trimmed) return null;
 if (/^-\d+$/.test(trimmed)) return "UDISE code cannot be negative";
 if (!/^\d+$/.test(trimmed)) return "UDISE code must contain only digits";
 return null;
}

// Latitude/longitude allow a decimal point (unlike UDISE), so "not a valid
// number at all" (letters, malformed input) and "a valid but negative
// number" are reported as two distinct, specific errors.
function getCoordinateError(raw, label) {
 const trimmed = String(raw).trim();
 if (!trimmed) return null;
 if (!/^-?\d+(\.\d+)?$/.test(trimmed)) return `${label} must be a valid number`;
 if (Number(trimmed) < 0) return `${label} cannot be negative`;
 return null;
}

function Schools() {
 const { canManageSchools, role } = useUserRole();
 const [data, setData] = useState([]);
 const [states, setStates] = useState([]);
 const [districts, setDistricts] = useState([]);
 const [blocks, setBlocks] = useState([]);
 const [loading, setLoading] = useState(true);
 /* ── views: "list" | "form" — backed by the URL so a refresh in edit mode
    reopens the same school instead of dropping back to the list ── */
 const [searchParams, setSearchParams] = useSearchParams();
 const view = searchParams.get("view") || "list";
 const urlEditId = searchParams.get("edit");
 const setView = useCallback((v, params = {}) => {
  if (v === "list") {
   setSearchParams({});
  } else if (v === "form" && params.edit) {
   setSearchParams({ view: "form", edit: params.edit });
  } else {
   setSearchParams({ view: v });
  }
 }, [setSearchParams]);
 const [editing, setEditing] = useState(null);
 const [formName, setFormName] = useState("");
 const [formUdiseCode, setFormUdiseCode] = useState("");
 const [formStateId, setFormStateId] = useState("");
 const [formDistrictId, setFormDistrictId] = useState("");
 const [formBlockId, setFormBlockId] = useState("");
 const [formIsAdopted, setFormIsAdopted] = useState(false);
 const [formLatitude, setFormLatitude] = useState("");
 const [formLongitude, setFormLongitude] = useState("");
 const [formIsActive, setFormIsActive] = useState(true);
 const [search, setSearch] = useState("");
 const debouncedSearch = useDebounce(search, 400);

 // Students list (read-only, shown while editing an existing school)
 const [studentsList, setStudentsList] = useState([]);
 const [studentsLoading, setStudentsLoading] = useState(false);
 const [studentSearch, setStudentSearch] = useState("");
 const debouncedStudentSearch = useDebounce(studentSearch, 400);
 const [studentGradeFilter, setStudentGradeFilter] = useState("");
 const [studentsPage, setStudentsPage] = useState(1);
 const [studentsPageSize, setStudentsPageSize] = useState(20);
 const [studentsTotalCount, setStudentsTotalCount] = useState(0);
 const [studentsTotalPages, setStudentsTotalPages] = useState(1);
 const [selectedIds, setSelectedIds] = useState([]);
 const [showBulkConfirm, setShowBulkConfirm] = useState(false);
 const [bulkLoading, setBulkLoading] = useState(false);
 const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
 const [toast, setToast] = useState(null);
 const selectAllRef = useRef(null);
 const fileInputRef = useRef(null);
 const [showUploadModal, setShowUploadModal] = useState(false);
 const [uploadFile, setUploadFile] = useState(null);
 const [uploadLoading, setUploadLoading] = useState(false);
 const [uploadResult, setUploadResult] = useState(null);

 // Pagination state
 const [currentPage, setCurrentPage] = useState(1);
 const [totalCount, setTotalCount] = useState(0);
 const [totalPages, setTotalPages] = useState(1);
 const [pageSize, setPageSize] = useState(20);

 // Filter state
 const [filters, setFilters] = useState({ state_id: "", district_id: "", block_id: "", is_adopted: "", is_active: "" });
 const [draftFilters, setDraftFilters] = useState({ state_id: "", district_id: "", block_id: "", is_adopted: "", is_active: "" });
 // The Filters panel's District/Block load lazily via async-select's own
 // fetchFn (below) instead of a prefetched list — unlike `districts`/`blocks`
 // above (still needed as-is for the create/edit modal's role-scoping), the
 // Filters panel has no modal-scoping to apply, so there's no reason to cap
 // or cache its options rather than searching/paginating the real endpoint.

 // Update indeterminate state of select-all checkbox
 useEffect(() => {
 if (selectAllRef.current) {
 const pageIds = data.map((item) => item.id);
 const selectedInView = selectedIds.filter((id) => pageIds.includes(id));
 selectAllRef.current.indeterminate =
 selectedInView.length > 0 && selectedInView.length < pageIds.length;
 }
 }, [selectedIds, data]);

 const loadData = useCallback(async (page = currentPage, searchQuery = debouncedSearch, activeFilters = filters) => {
 setLoading(true);
 try {
 const params = { page, page_size: pageSize, search: searchQuery };
 if (activeFilters.state_id) params.state_id = activeFilters.state_id;
 if (activeFilters.district_id) params.district_id = activeFilters.district_id;
 if (activeFilters.block_id) params.block_id = activeFilters.block_id;
 if (activeFilters.is_adopted !== "" && activeFilters.is_adopted !== undefined) params.is_adopted = activeFilters.is_adopted;
 if (activeFilters.is_active !== "" && activeFilters.is_active !== undefined) params.is_active = activeFilters.is_active;
 // Rows already carry state/district/block names — the lookup lists
 // below are loaded separately (once), not on every page/search/filter.
 const schoolsResult = await fetchSchools(params);
 setData(schoolsResult.data);
 setTotalCount(schoolsResult.total_count);
 setTotalPages(schoolsResult.total_pages);
 setCurrentPage(schoolsResult.current_page);
 } catch (err) {
 console.error("Failed to load schools", err);
 } finally {
 setLoading(false);
 }
 }, [currentPage, debouncedSearch, filters, pageSize]);

 useEffect(() => {
 setCurrentPage(1);
 loadData(1, debouncedSearch, filters);
 }, [debouncedSearch, filters, pageSize]);

 const handlePageSizeChange = (newSize) => {
 setPageSize(newSize);
 setCurrentPage(1);
 };

 // Load this school's students (read-only list shown in the edit form)
 const loadStudents = useCallback(async (schoolId, page = 1, searchQuery = "", grade = "", pageSize = 20) => {
 setStudentsLoading(true);
 try {
 const res = await fetchStudents({ school_id: schoolId, page, page_size: pageSize, search: searchQuery, grade: grade || undefined });
 setStudentsList(res.data || []);
 setStudentsTotalCount(res.total_count || 0);
 setStudentsTotalPages(Math.max(1, Math.ceil((res.total_count || 0) / pageSize)));
 } catch (err) {
 console.error("Failed to load students", err);
 setStudentsList([]);
 setStudentsTotalCount(0);
 setStudentsTotalPages(1);
 } finally {
 setStudentsLoading(false);
 }
 }, []);

 useEffect(() => {
 if (view === "form" && editing?.id) {
 setStudentsPage(1);
 loadStudents(editing.id, 1, debouncedStudentSearch, studentGradeFilter, studentsPageSize);
 }
 }, [view, editing, debouncedStudentSearch, studentGradeFilter, studentsPageSize, loadStudents]);

 const handleStudentsPageSizeChange = (newSize) => {
 setStudentsPageSize(newSize);
 setStudentsPage(1);
 };

 // States: State filter options + SPL/PL/PM form dropdown — load once.
 useEffect(() => {
 fetchMasterData("states", { page: 1, page_size: 100, search: "" })
 .then((res) => setStates(res.data || []))
 .catch((err) => console.error("Failed to load states", err));
 }, []);

 // Districts/blocks lists only feed the PL (district + block) and PM (block)
 // plain <select>s in the form — every other dropdown fetches its own options.
 // Load them the first time the form opens for those roles, then reuse.
 const formListsLoaded = useRef(false);
 useEffect(() => {
 if (view !== "form" || formListsLoaded.current) return;
 if (role !== "PL" && role !== "PM") return;
 formListsLoaded.current = true;
 fetchMasterData("blocks", { page: 1, page_size: 100, search: "" })
 .then((res) => setBlocks(res.data || []))
 .catch((err) => console.error("Failed to load blocks", err));
 if (role === "PL") {
 fetchMasterData("districts", { page: 1, page_size: 100, search: "" })
 .then((res) => setDistricts(res.data || []))
 .catch((err) => console.error("Failed to load districts", err));
 }
 }, [view, role]);

 const getBlockName = (item) => {
 return item.block_name || "—";
 };

 const getDistrictName = (item) => {
 return item.district_name || "—";
 };

 const getStateName = (item) => {
 return item.state_name || "—";
 };

 // The states/districts/blocks lists and the schools list are already
 // role-scoped server-side (SPL: own states, PL: own districts, PM: own
 // blocks), so every listed row is within the caller's area.
 const modalStates = states;
 const modalDistricts = districts;
 const modalBlocks = blocks;

 const canDeleteItem = () => ["ADMIN", "SPL", "PL", "PM"].includes(role);

 const filteredDistricts = formStateId
 ? modalDistricts.filter((d) => d.state_id === Number(formStateId))
 : [];

 const filteredBlocks = formDistrictId
 ? modalBlocks.filter((b) => b.district_id === Number(formDistrictId))
 : [];

 const openCreate = () => {
 setEditing(null);
 setFormName("");
 setFormUdiseCode("");
 setFormStateId("");
 setFormDistrictId("");
 setFormBlockId("");
 setFormIsAdopted(false);
 setFormLatitude("");
 setFormLongitude("");
 setFormIsActive(true);
 setView("form");
 };

 const openEdit = (item) => {
 // A direct click already has the full item — mark the URL-restore effect
 // as satisfied so it doesn't also redundantly re-fetch this same item.
 hasRestoredFromUrl.current = true;
 setEditing(item);
 setStudentSearch("");
 setStudentGradeFilter("");
 setStudentsPageSize(20);
 setStudentsList([]);
 setFormName(item.name);
 setFormUdiseCode(item.udise_code || "");
 setFormStateId(item.state_id || "");
 setFormDistrictId(item.district_id || "");
 setFormBlockId(item.block_id);
 setFormIsAdopted(item.is_adopted || false);
 setFormLatitude(item.latitude != null ? String(item.latitude) : "");
 setFormLongitude(item.longitude != null ? String(item.longitude) : "");
 setFormIsActive(item.is_active !== undefined ? item.is_active : true);
 setView("form", { edit: item.id });
 };

 // Restore edit view from URL on refresh — the single-school GET only returns
 // block_id (no district_id/state_id), so chain real by-id lookups
 // (school -> block -> district) instead of relying on the blocks/districts
 // lists in state, which are page-capped and may not contain this row, and
 // may still be loading (which would otherwise stall this effect forever).
 const hasRestoredFromUrl = useRef(false);
 useEffect(() => {
 if (hasRestoredFromUrl.current) return;
 if (view === "form" && urlEditId) {
 hasRestoredFromUrl.current = true;
 fetchSchoolById(urlEditId)
 .then(async (res) => {
 const item = res.data || {};
 let districtId, stateId;
 if (item.block_id) {
 try {
 const blockRes = await fetchBlockById(item.block_id);
 districtId = blockRes.data?.district_id;
 if (districtId) {
 const districtRes = await fetchDistrictById(districtId);
 stateId = districtRes.data?.state_id;
 }
 } catch {
 // leave blank — user can still pick district/state manually
 }
 }
 openEdit({ ...item, district_id: districtId, state_id: stateId });
 })
 .catch(() => setView("list"));
 } else if (view === "form" && !urlEditId) {
 hasRestoredFromUrl.current = true;
 openCreate();
 }
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [view, urlEditId]);

 const handleSubmit = async (e) => {
 e.preventDefault();
 if (!formStateId) {
 setToast({ message: "Please select a state", type: "error" });
 return;
 }
 if (!formDistrictId) {
 setToast({ message: "Please select a district", type: "error" });
 return;
 }
 if (!formBlockId) {
 setToast({ message: "Please select a block", type: "error" });
 return;
 }
 if (!formUdiseCode.trim()) {
 setToast({ message: "UDISE code is required", type: "error" });
 return;
 }
 const udiseError = getUdiseCodeError(formUdiseCode);
 if (udiseError) {
 setToast({ message: udiseError, type: "error" });
 return;
 }
 const latitudeError = getCoordinateError(formLatitude, "Latitude");
 if (latitudeError) {
 setToast({ message: latitudeError, type: "error" });
 return;
 }
 const longitudeError = getCoordinateError(formLongitude, "Longitude");
 if (longitudeError) {
 setToast({ message: longitudeError, type: "error" });
 return;
 }
 const trimmedName = formName.trim();
 if (!/\p{L}/u.test(trimmedName)) {
 setToast({ message: "Name must contain at least one letter (not just numbers, spaces, or symbols)", type: "error" });
 return;
 }
 try {
 const baseData = {
 name: trimmedName,
 block_id: Number(formBlockId),
 udise_code: formUdiseCode.trim(),
 is_adopted: formIsAdopted,
 latitude: formLatitude !== "" ? parseFloat(formLatitude) : null,
 longitude: formLongitude !== "" ? parseFloat(formLongitude) : null,
 };
 if (editing) {
 await updateSchool(editing.id, { ...baseData, is_active: formIsActive });
 setView("list");
 } else {
 await createSchool(baseData);
 setView("list");
 }
 loadData(currentPage, debouncedSearch);
 } catch (err) {
 console.error("Save failed", err);
 setToast({ message: apiErrorMessage(err, "Operation failed. Please try again."), type: "error" });
 }
 };

 const handleDelete = async (id) => {
 try {
  await deleteSchool(id);
  setShowDeleteConfirm(null);
  setSelectedIds((prev) => prev.filter((sid) => sid !== id));
  if (view !== "list") setView("list");
  loadData(currentPage, debouncedSearch);
 } catch (err) {
  console.error("Delete failed", err);
  setShowDeleteConfirm(null);
  const msg = err.response?.data?.detail || "Failed to delete school. Please try again.";
  setToast({ message: msg, type: "error" });
 }
 };

 /* Bulk Selection Handlers */
 const handleSelectRow = (id) => {
 setSelectedIds((prev) =>
 prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
 );
 };

 const handleSelectAll = () => {
 const pageIds = data.filter((item) => canDeleteItem(item)).map((item) => item.id);
 const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
 if (allSelected) {
 setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
 } else {
 setSelectedIds((prev) => [...new Set([...prev, ...pageIds])]);
 }
 };

 const clearSelection = () => setSelectedIds([]);

 const handleBulkDelete = async () => {
 if (bulkLoading) return;
 setBulkLoading(true);
 try {
 const res = await bulkActionSchools("delete", selectedIds);
 setShowBulkConfirm(false);
 setSelectedIds([]);
 setToast({
 message: res.message || `Successfully deleted ${res.processed} school(s).`,
 type: "success",
 });
 loadData(currentPage, debouncedSearch);
 } catch (err) {
 console.error("Bulk delete failed", err);
 const msg =
 err.response?.data?.detail || "Bulk delete failed. Please try again.";
 setToast({ message: msg, type: "error" });
 setShowBulkConfirm(false);
 } finally {
 setBulkLoading(false);
 }
 };

 /* Bulk Upload Handlers */
 const openUploadModal = () => {
 setUploadFile(null);
 setUploadResult(null);
 setShowUploadModal(true);
 };

 const handleFileChange = (e) => {
 const f = e.target.files[0];
 if (f && !f.name.endsWith(".xlsx")) {
 setToast({ message: "Only .xlsx files are accepted", type: "error" });
 return;
 }
 setUploadFile(f || null);
 setUploadResult(null);
 };

 const handleUpload = async () => {
 if (!uploadFile || uploadLoading) return;
 setUploadLoading(true);
 setUploadResult(null);
 try {
 const res = await bulkUpload("schools", uploadFile);
 setUploadResult(res);
 if (res.success) {
 setToast({ message: res.message || `Inserted ${res.inserted} school(s)`, type: "success" });
 loadData(currentPage, debouncedSearch);
 }
 } catch (err) {
 const detail = err.response?.data?.detail || "Upload failed";
 setUploadResult({ success: false, errors: [], message: detail });
 setToast({ message: detail, type: "error" });
 } finally {
 setUploadLoading(false);
 }
 };

 const downloadTemplate = () => {
 const { downloadXlsxTemplate } = require("../services/xlsxTemplate");
 downloadXlsxTemplate("schools_template.xlsx", ["name", "udise_code", "block_name", "is_adopted", "latitude", "longitude"], [["ABC Public School", "09010100101", "Sadar", "Yes", "26.8467", "80.9462"], ["XYZ Academy", "09010100102", "Gomti Nagar", "No", "26.8505", "80.9553"]]);
 };

 const renderTableContent = () => {
 if (loading) {
 return (
 <div className="p-8 space-y-4">
 {[1, 2, 3, 4].map((i) => (
 <div key={i} className="flex items-center gap-4 animate-pulse">
 <div className="w-5 h-5 bg-[#e5e7eb] rounded" />
 <div className="w-8 h-4 bg-[#e5e7eb] rounded" />
 <div className="flex-1 h-4 bg-[#e5e7eb] rounded" />
 <div className="w-24 h-4 bg-[#e5e7eb] rounded" />
 <div className="w-24 h-4 bg-[#e5e7eb] rounded" />
 <div className="w-24 h-4 bg-[#e5e7eb] rounded" />
 <div className="w-20 h-4 bg-[#e5e7eb] rounded" />
 </div>
 ))}
 </div>
 );
 }
 if (data.length === 0) {
 return (
 <div className="text-center py-16 px-5">
 <div className="w-16 h-16 bg-[#f3f4f6] rounded-lg flex items-center justify-center mx-auto mb-4">
 <HiOutlineAcademicCap className="text-2xl text-[#8b949e]" />
 </div>
 <p className="m-0 text-sm font-medium text-[#57606a]">
 {search ? "No schools match your search." : "No schools found."}
 </p>
 <p className="m-0 text-xs text-[#8b949e] mt-1">
 {search ? "Try a different search term." : "Create your first school to get started."}
 </p>
 </div>
 );
 }

 const pageIds = data.map((item) => item.id);
 const allSelected =
 pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));

 return (
 <table className="w-full text-sm">
 <thead>
 <tr className="bg-[#f6f8fa] border-b border-[#e5e7eb]">
 {canManageSchools && (
 <th className="w-12 text-center py-4 px-3">
 <input
 ref={selectAllRef}
 type="checkbox"
 className="w-4 h-4 rounded border-[#d0d5dd] text-[#0078d4] focus:ring-[#0078d4] cursor-pointer accent-[#0078d4]"
 checked={allSelected}
 onChange={handleSelectAll}
 title="Select all"
 />
 </th>
 )}
 <th className="text-left py-4 px-6 text-xs font-medium text-[#57606a] w-[60px]">#</th>
 <th className="text-left py-4 px-6 text-xs font-medium text-[#57606a]">School Name</th>
 <th className="text-left py-4 px-6 text-xs font-medium text-[#57606a]">UDISE Code</th>
 <th className="text-left py-4 px-6 text-xs font-medium text-[#57606a]">Block</th>
 <th className="text-left py-4 px-6 text-xs font-medium text-[#57606a]">District</th>
 <th className="text-left py-4 px-6 text-xs font-medium text-[#57606a]">State</th>
 <th className="text-center py-4 px-6 text-xs font-medium text-[#57606a]">Adopted</th>
 <th className="text-center py-4 px-6 text-xs font-medium text-[#57606a]">Total Students</th>
 <th className="text-center py-4 px-6 text-xs font-medium text-[#57606a]">Status</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-[#e5e7eb]">
 {data.map((item, idx) => {
 const isSelected = selectedIds.includes(item.id);
 return (
 <tr
 key={item.id}
 className={`group transition-colors duration-150 cursor-pointer ${
 isSelected
 ? "bg-[#eff6ff] hover:bg-[#eff6ff]"
 : "hover:bg-gray-50"
 }`}
 onClick={() => openEdit(item)}
 >
 {canManageSchools && (
 <td className="w-12 text-center py-4 px-3" onClick={(e) => e.stopPropagation()}>
 {canDeleteItem(item) ? (
 <input
 type="checkbox"
 className="w-[18px] h-[18px] rounded border-[#d0d5dd] text-[#0078d4] focus:ring-[#0078d4] cursor-pointer accent-[#0078d4]"
 checked={isSelected}
 onChange={() => handleSelectRow(item.id)}
 />
 ) : null}
 </td>
 )}
 <td className="py-4 px-6 text-[#8b949e] font-medium text-xs">{(currentPage - 1) * pageSize + idx + 1}</td>
 <td className="py-4 px-6">
 <div className="flex items-center gap-3">
 <div className="w-8 h-8 bg-[#eff6ff] rounded-md flex items-center justify-center flex-shrink-0">
 <HiOutlineAcademicCap className="text-sm text-[#0078d4]" />
 </div>
 <div>
 <span className="font-medium text-[#24292f]">{item.name}</span>
 </div>
 </div>
 </td>
 <td className="py-4 px-6">
 <span className="text-xs font-mono text-[#57606a]">{item.udise_code || "—"}</span>
 </td>
 <td className="py-4 px-6">
 <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-[#f3f4f6] text-[#57606a] rounded">
 {getBlockName(item)}
 </span>
 </td>
 <td className="py-4 px-6">
 <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-[#f3f4f6] text-[#57606a] rounded">
 {getDistrictName(item)}
 </span>
 </td>
 <td className="py-4 px-6">
 <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-[#f3f4f6] text-[#57606a] rounded">
 {getStateName(item)}
 </span>
 </td>
 <td className="py-4 px-6 text-center">
 {item.is_adopted ? (
 <span className="inline-flex items-center px-2.5 py-1 text-xs font-semibold bg-blue-50 text-blue-700 rounded-full">
 Yes
 </span>
 ) : (
 <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-[#f3f4f6] text-[#8b949e] rounded-full">
 No
 </span>
 )}
 </td>
 <td className="py-4 px-6 text-center text-[#24292f] font-medium">
 {item.student_count ?? 0}
 </td>
 <td className="py-4 px-6 text-center">
 <span className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full ${
 item.is_active !== false
 ? "bg-green-50 text-green-700"
 : "bg-[#f3f4f6] text-[#8b949e]"
 }`}>
 {item.is_active !== false ? "Active" : "Inactive"}
 </span>
 </td>
 </tr>
 );
 })}
 </tbody>
 </table>
 );
 };

 return (
 <div className="p-6">
 {toast && (
 <Toast
 message={toast.message}
 type={toast.type}
 onClose={() => setToast(null)}
 />
 )}

 {/* List View */}
 {view === "list" && (
 <div className="bg-white rounded-lg border border-[#e5e7eb] overflow-x-auto">
 {/* Toolbar */}
 <div className="flex justify-between items-center px-6 py-4 border-b border-[#e5e7eb]">
 <div className="relative flex items-center flex-1 max-w-sm">
 <HiOutlineSearch className="absolute left-3.5 text-[#8b949e] text-sm" />
 <input
 type="text"
 className="w-full py-2.5 pr-8 pl-10 text-sm border border-[#d0d5dd] rounded-md text-[#24292f] bg-white transition-all duration-200 box-border placeholder:text-[#8b949e] focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10"
 placeholder="Search schools…"
 value={search}
 onChange={(e) => setSearch(e.target.value)}
 />
 {search && (
 <button
 className="absolute right-2 bg-transparent border-none text-[#8b949e] cursor-pointer p-1 rounded-md hover:text-[#57606a] hover:bg-[#f3f4f6] transition-colors"
 onClick={() => setSearch("")}
 >
 <HiOutlineX className="text-sm" />
 </button>
 )}
 </div>
 <div className="flex items-center gap-2">
 <FilterDropdown
 filters={[
 {
 key: "state_id",
 label: "State",
 type: "select",
 options: states.map((s) => ({ value: String(s.id), label: s.name })),
 },
 {
 key: "district_id",
 label: "District",
 type: "async-select",
 pageSize: 20,
 scopeKey: draftFilters.state_id ? `state:${draftFilters.state_id}` : "all",
 fetchFn: (search, page) => fetchMasterData("districts", {
 page, page_size: 20, search,
 state_id: draftFilters.state_id || undefined,
 }).then((res) => ({ data: (res.data || []).map((d) => ({ value: String(d.id), label: d.name })), total_count: res.total_count || 0 })),
 },
 {
 key: "block_id",
 label: "Block",
 type: "async-select",
 pageSize: 20,
 scopeKey: draftFilters.district_id ? `district:${draftFilters.district_id}` : "all",
 fetchFn: (search, page) => fetchMasterData("blocks", {
 page, page_size: 20, search,
 district_id: draftFilters.district_id || undefined,
 }).then((res) => ({ data: (res.data || []).map((b) => ({ value: String(b.id), label: b.name })), total_count: res.total_count || 0 })),
 },
 {
 key: "is_adopted",
 label: "Adopted",
 type: "select",
 options: [
 { value: "true", label: "Yes" },
 { value: "false", label: "No" },
 ],
 },
 {
 key: "is_active",
 label: "Status",
 type: "select",
 options: [
 { value: "true", label: "Active" },
 { value: "false", label: "Inactive" },
 ],
 },
 ]}
 values={filters}
 onChange={(newFilters) => {
 setFilters(newFilters);
 setDraftFilters(newFilters);
 setCurrentPage(1);
 }}
 onDraftChange={(draft) => setDraftFilters(draft)}
 />
 {canManageSchools && (
 <>
 <button
 className="inline-flex items-center gap-2 h-10 px-5 text-sm font-semibold rounded-md cursor-pointer transition-all duration-200 bg-white text-[#24292f] border border-[#d0d5dd] hover:bg-[#f3f4f6] hover:border-[#8b949e]"
 onClick={openUploadModal}
 >
 <HiOutlineUpload className="text-base" />
 Bulk Upload
 </button>
 <button
 className="inline-flex items-center gap-2 h-10 px-5 text-sm font-semibold border-none rounded-md cursor-pointer transition-all duration-200 bg-[#0078d4] text-white hover:bg-[#106ebe]"
 onClick={openCreate}
 >
 <HiOutlinePlus className="text-base" />
 Add School
 </button>
 </>
 )}
 </div>
 </div>

 {/* Bulk Action Toolbar */}
 {canManageSchools && selectedIds.length > 0 && (
 <div className="flex items-center justify-between px-6 py-3 bg-[#eff6ff] border-b border-[#d0d5dd] animate-[fadeIn_0.2s_ease-out]">
 <div className="flex items-center gap-3">
 <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#0078d4] text-white text-xs font-bold">
 {selectedIds.length}
 </span>
 <span className="text-sm font-medium text-[#24292f]">
 {selectedIds.length === 1 ? "item selected" : "items selected"}
 </span>
 </div>
 <div className="flex items-center gap-2">
 <button
 className="inline-flex items-center gap-1.5 py-2 px-4 text-sm font-semibold rounded-lg cursor-pointer transition-all duration-200 bg-white text-[#57606a] border border-[#d0d5dd] hover:bg-[#f3f4f6]"
 onClick={clearSelection}
 >
 Clear
 </button>
 <button
 className="inline-flex items-center gap-1.5 py-2 px-4 text-sm font-semibold rounded-lg cursor-pointer transition-all duration-200 bg-red-600 text-white border-none hover:bg-red-700 shadow-sm"
 onClick={() => setShowBulkConfirm(true)}
 >
 <HiOutlineTrash className="text-sm" />
 Delete Selected
 </button>
 </div>
 </div>
 )}

 {/* Table */}
 {renderTableContent()}

 {/* Pagination */}
 {!loading && totalCount > 0 && (
 <Pagination
 currentPage={currentPage}
 totalPages={totalPages}
 totalCount={totalCount}
 pageSize={pageSize}
 onPageChange={(p) => loadData(p, debouncedSearch, filters)}
 onPageSizeChange={handlePageSizeChange}
 entityName="schools"
 />
 )}
 </div>
 )}

 {/* Create/Edit Form */}
 {view === "form" && (() => {
 const formEditable = canManageSchools;
 return (
 <div className="max-w-7xl mx-auto">
 <div className="bg-white rounded-xl border border-[#e5e7eb] w-full">
 {/* Form Header */}
 <div className="sticky top-14 z-40 flex justify-between items-center px-6 py-5 border-b border-[#e5e7eb] bg-white rounded-t-xl">
 <div className="flex items-center gap-3">
 <button
 onClick={() => setView("list")}
 className="w-8 h-8 flex items-center justify-center rounded-md text-[#57606a] hover:bg-[#f3f4f6] transition-all border-none bg-transparent cursor-pointer"
 title="Back"
 >
 <HiOutlineChevronLeft className="text-lg" />
 </button>
 <div>
 <h3 className="m-0 text-[15px] font-semibold text-[#24292f]">
 {!formEditable ? "School Details" : editing ? "Edit School" : "Add New School"}
 </h3>
 <p className="m-0 mt-1 text-[13px] text-[#8b949e]">
 {!formEditable ? "View school information" : editing ? "Update school information" : "Create a new school entry"}
 </p>
 </div>
 </div>
 {formEditable && editing && (
 <button onClick={() => setShowDeleteConfirm(editing)} className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700">Delete</button>
 )}
 </div>

 <form onSubmit={handleSubmit} className="flex flex-col">
 <div className="p-6 space-y-5">
 <div>
 <label htmlFor="school-state" className="block mb-2 text-sm font-medium text-[#24292f]">
 State <span className="text-red-500">*</span>
 </label>
 <SearchableDropdown
 value={String(formStateId || "")}
 onChange={(id) => {
 setFormStateId(id);
 setFormDistrictId("");
 setFormBlockId("");
 }}
 placeholder="Select a state"
 fetchFn={role === "ADMIN"
 ? (search, page = 1) => fetchMasterData("states", { page, page_size: 20, search })
 : staticFetchFn(modalStates)}
 cacheNamespace="school_state_id"
 disabled={!formEditable}
 variant="form"
 />
 </div>
 <div>
 <label htmlFor="school-district" className="block mb-2 text-sm font-medium text-[#24292f]">
 District <span className="text-red-500">*</span>
 </label>
 {role === "PL" ? (
  <select
   id="school-district"
   className="w-full py-2.5 px-4 pr-10 text-sm border border-[#d0d5dd] rounded-md text-[#24292f] bg-white transition-all duration-200 box-border focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10 disabled:bg-[#f3f4f6] disabled:text-[#8b949e] disabled:cursor-not-allowed"
   value={formDistrictId}
   onChange={(e) => { setFormDistrictId(e.target.value); setFormBlockId(""); }}
   required
   disabled={!formStateId || !formEditable}
  >
   <option value="">{formStateId ? "Select a district" : "Select a state first"}</option>
   {filteredDistricts.map((d) => (<option key={d.id} value={d.id}>{d.name}</option>))}
  </select>
 ) : (
  <SearchableDropdown
   key={`sch-dist-${formStateId}`}
   value={String(formDistrictId)}
   onChange={(id) => { setFormDistrictId(id); setFormBlockId(""); }}
   placeholder={formStateId ? "Select a district" : "Select a state first"}
   fetchFn={(search, page = 1) => fetchMasterData("districts", {
    page, page_size: 20,
    state_id: Number(formStateId),
    search,
   })}
   disabled={!formStateId || !formEditable}
   variant="form"
  />
 )}
 </div>
 <div>
 <label htmlFor="school-block" className="block mb-2 text-sm font-medium text-[#24292f]">
 Block <span className="text-red-500">*</span>
 </label>
 {(role === "PL" || role === "PM") ? (
  <select
   id="school-block"
   className="w-full py-2.5 px-4 pr-10 text-sm border border-[#d0d5dd] rounded-md text-[#24292f] bg-white transition-all duration-200 box-border focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10 disabled:bg-[#f3f4f6] disabled:text-[#8b949e] disabled:cursor-not-allowed"
   value={formBlockId}
   onChange={(e) => setFormBlockId(e.target.value)}
   required
   disabled={!formDistrictId || !formEditable}
  >
   <option value="">{formDistrictId ? "Select a block" : "Select a district first"}</option>
   {filteredBlocks.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
  </select>
 ) : (
  <SearchableDropdown
   key={`sch-blk-${formDistrictId}`}
   value={String(formBlockId)}
   onChange={(id) => setFormBlockId(id)}
   placeholder={formDistrictId ? "Select a block" : "Select a district first"}
   fetchFn={(search, page = 1) => fetchMasterData("blocks", {
    page, page_size: 20,
    district_id: Number(formDistrictId),
    search,
   })}
   disabled={!formDistrictId || !formEditable}
   variant="form"
  />
 )}
 </div>
 <div>
 <label htmlFor="school-name" className="block mb-2 text-sm font-medium text-[#24292f]">
 School Name <span className="text-red-500">*</span>
 </label>
 <input
 id="school-name"
 className="w-full py-2.5 px-4 text-sm border border-[#d0d5dd] rounded-md text-[#24292f] bg-white transition-all duration-200 box-border focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10"
 type="text"
 placeholder="Enter school name"
 value={formName}
 onChange={(e) => setFormName(e.target.value)}
 disabled={!formEditable}
 required
 />
 </div>
 <div>
 <label htmlFor="school-udise" className="block mb-2 text-sm font-medium text-[#24292f]">
 UDISE Code <span className="text-red-500">*</span>
 </label>
 <input
 id="school-udise"
 className="w-full py-2.5 px-4 text-sm border border-[#d0d5dd] rounded-md text-[#24292f] bg-white transition-all duration-200 box-border font-mono focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10"
 type="text"
 inputMode="numeric"
 placeholder="Enter UDISE code"
 value={formUdiseCode}
 onChange={(e) => setFormUdiseCode(e.target.value)}
 disabled={!formEditable}
 required
 />
 {getUdiseCodeError(formUdiseCode) && (
 <p className="m-0 mt-1 text-xs text-red-500">{getUdiseCodeError(formUdiseCode)}</p>
 )}
 </div>
 <div className="grid grid-cols-2 gap-4">
 <div>
 <label htmlFor="school-lat" className="block mb-2 text-sm font-medium text-[#24292f]">
 Latitude
 </label>
 <input
 id="school-lat"
 className="w-full py-2.5 px-4 text-sm border border-[#d0d5dd] rounded-md text-[#24292f] bg-white transition-all duration-200 box-border focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10"
 type="text"
 inputMode="decimal"
 placeholder="e.g. 26.8467"
 value={formLatitude}
 onChange={(e) => setFormLatitude(e.target.value)}
 disabled={!formEditable}
 />
 {getCoordinateError(formLatitude, "Latitude") && (
 <p className="m-0 mt-1 text-xs text-red-500">{getCoordinateError(formLatitude, "Latitude")}</p>
 )}
 </div>
 <div>
 <label htmlFor="school-lng" className="block mb-2 text-sm font-medium text-[#24292f]">
 Longitude
 </label>
 <input
 id="school-lng"
 className="w-full py-2.5 px-4 text-sm border border-[#d0d5dd] rounded-md text-[#24292f] bg-white transition-all duration-200 box-border focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10"
 type="text"
 inputMode="decimal"
 placeholder="e.g. 80.9462"
 value={formLongitude}
 onChange={(e) => setFormLongitude(e.target.value)}
 disabled={!formEditable}
 />
 {getCoordinateError(formLongitude, "Longitude") && (
 <p className="m-0 mt-1 text-xs text-red-500">{getCoordinateError(formLongitude, "Longitude")}</p>
 )}
 </div>
 </div>
 <div className="flex items-center justify-between py-2">
 <div>
 <label className="block text-sm font-medium text-[#24292f]">Is Adopted</label>
 <p className="m-0 text-xs text-[#8b949e] mt-0.5">Mark if this school is adopted</p>
 </div>
 <button
 type="button"
 className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#0078d4]/10 ${
 formIsAdopted ? "bg-[#0078d4]" : "bg-[#d0d5dd]"
 }`}
 onClick={() => setFormIsAdopted(!formIsAdopted)}
 disabled={!formEditable}
 >
 <span
 className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
 formIsAdopted ? "translate-x-6" : "translate-x-1"
 }`}
 />
 </button>
 </div>
 {editing && (
 <div className="flex items-center justify-between py-2">
 <div>
 <label className="block text-sm font-medium text-[#24292f]">Is Active</label>
 <p className="m-0 text-xs text-[#8b949e] mt-0.5">Toggle school active status</p>
 </div>
 <button
 type="button"
 className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#0078d4]/10 ${
 formIsActive ? "bg-[#0078d4]" : "bg-[#d0d5dd]"
 }`}
 onClick={() => setFormIsActive(!formIsActive)}
 disabled={!formEditable}
 >
 <span
 className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
 formIsActive ? "translate-x-6" : "translate-x-1"
 }`}
 />
 </button>
 </div>
 )}
 </div>

 {editing && (
 <div className="border border-[#e5e7eb] rounded-lg overflow-hidden">
 <div className="flex justify-between items-center px-4 py-3 border-b border-[#e5e7eb] bg-[#f6f8fa]">
 <span className="text-sm font-medium text-[#24292f]">Students ({studentsTotalCount})</span>
 <div className="flex items-center gap-2">
 <select
 className="py-1.5 px-3 text-sm border border-[#d0d5dd] rounded-md text-[#24292f] bg-white focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10"
 value={studentGradeFilter}
 onChange={(e) => setStudentGradeFilter(e.target.value)}
 >
 <option value="">All Grades</option>
 {[1, 2, 3, 4, 5, 6, 7, 8].map((g) => (
 <option key={g} value={g}>Grade {g}</option>
 ))}
 </select>
 <div className="relative flex items-center w-64">
 <HiOutlineSearch className="absolute left-3 text-[#8b949e] text-sm" />
 <input
 type="text"
 className="w-full py-1.5 pr-7 pl-9 text-sm border border-[#d0d5dd] rounded-md text-[#24292f] bg-white transition-all duration-200 box-border placeholder:text-[#8b949e] focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10"
 placeholder="Search students…"
 value={studentSearch}
 onChange={(e) => setStudentSearch(e.target.value)}
 />
 {studentSearch && (
 <button
 type="button"
 className="absolute right-1.5 bg-transparent border-none text-[#8b949e] cursor-pointer p-1 rounded-md hover:text-[#57606a] hover:bg-[#f3f4f6] transition-colors"
 onClick={() => setStudentSearch("")}
 >
 <HiOutlineX className="text-sm" />
 </button>
 )}
 </div>
 </div>
 </div>
 <table className="w-full text-sm">
 <thead>
 <tr className="bg-[#f6f8fa] border-b border-[#e5e7eb]">
 <th className="text-left py-3 px-4 text-xs font-medium text-[#57606a]">SRN</th>
 <th className="text-left py-3 px-4 text-xs font-medium text-[#57606a]">Name</th>
 <th className="text-left py-3 px-4 text-xs font-medium text-[#57606a]">Gender</th>
 <th className="text-left py-3 px-4 text-xs font-medium text-[#57606a]">Grade</th>
 <th className="text-left py-3 px-4 text-xs font-medium text-[#57606a]">Roll No</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-[#e5e7eb]">
 {studentsLoading ? (
 [1, 2, 3].map((i) => (
 <tr key={i} className="animate-pulse">
 <td className="py-3 px-4" colSpan={5}><div className="h-4 bg-[#f0f0f0] rounded" /></td>
 </tr>
 ))
 ) : studentsList.length === 0 ? (
 <tr>
 <td colSpan={5} className="text-center py-8 text-sm text-[#8b949e]">
 {studentSearch || studentGradeFilter ? "No students match your search." : "No students in this school."}
 </td>
 </tr>
 ) : (
 studentsList.map((s) => (
 <tr key={s.id}>
 <td className="py-3 px-4 text-[#57606a]">{s.srn}</td>
 <td className="py-3 px-4 text-[#24292f] font-medium">{s.name || "—"}</td>
 <td className="py-3 px-4 text-[#57606a]">{s.gender || "—"}</td>
 <td className="py-3 px-4 text-[#57606a]">{s.grade ?? "—"}</td>
 <td className="py-3 px-4 text-[#57606a]">{s.roll_no ?? "—"}</td>
 </tr>
 ))
 )}
 </tbody>
 </table>
 {!studentsLoading && studentsTotalCount > 0 && (
 <Pagination
 currentPage={studentsPage}
 totalPages={studentsTotalPages}
 totalCount={studentsTotalCount}
 pageSize={studentsPageSize}
 onPageChange={(p) => { setStudentsPage(p); loadStudents(editing.id, p, debouncedStudentSearch, studentGradeFilter, studentsPageSize); }}
 onPageSizeChange={handleStudentsPageSizeChange}
 entityName="students"
 />
 )}
 </div>
 )}

 <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#e5e7eb] bg-[#f6f8fa] rounded-b-lg">
 <button
 type="button"
 className="py-2.5 px-5 text-sm font-semibold rounded-md cursor-pointer transition-all duration-200 bg-white text-[#57606a] border border-[#d0d5dd] hover:bg-[#f3f4f6]"
 onClick={() => setView("list")}
 >
 {formEditable ? "Cancel" : "Back"}
 </button>
 {formEditable && (
 <button
 type="submit"
 className="py-2.5 px-5 text-sm font-semibold border-none rounded-md cursor-pointer transition-all duration-200 bg-[#0078d4] text-white hover:bg-[#106ebe]"
 >
 {editing ? "Save Changes" : "Create School"}
 </button>
 )}
 </div>
 </form>
 </div>
 </div>
 );
 })()}

 {/* Single Delete Confirmation Modal */}
 {showDeleteConfirm && (
 <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[250] p-4" onClick={() => setShowDeleteConfirm(null)}>
  <div className="bg-white rounded-lg border border-[#e5e7eb] w-full max-w-sm shadow-[0_8px_24px_rgba(0,0,0,0.12)] animate-[fadeIn_0.2s_ease-out]" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
   <div className="p-6 text-center">
    <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mx-auto mb-4"><HiOutlineExclamation className="text-2xl text-red-600" /></div>
    <h3 className="m-0 text-lg font-semibold text-[#24292f]">Delete School?</h3>
    <p className="m-0 mt-2 text-sm text-[#57606a] leading-relaxed">Are you sure you want to delete <strong>{showDeleteConfirm.name}</strong>? This action cannot be undone.</p>
   </div>
   <div className="flex gap-3 px-6 py-4 border-t border-[#e5e7eb] bg-[#f6f8fa] rounded-b-lg">
    <button className="flex-1 py-2.5 px-5 text-sm font-semibold rounded-md cursor-pointer transition-all duration-200 bg-white text-[#57606a] border border-[#d0d5dd] hover:bg-[#f3f4f6]" onClick={() => setShowDeleteConfirm(null)}>Cancel</button>
    <button className="flex-1 py-2.5 px-5 text-sm font-semibold border-none rounded-md cursor-pointer transition-all duration-200 bg-red-600 text-white hover:bg-red-700 shadow-sm" onClick={() => handleDelete(showDeleteConfirm.id)}>Delete</button>
   </div>
  </div>
 </div>
 )}

 {/* Bulk Delete Confirmation Modal */}
 {showBulkConfirm && (
 <div
 className="fixed inset-0 bg-black/40 flex items-center justify-center z-[250] p-4"
 onClick={() => !bulkLoading && setShowBulkConfirm(false)}
 >
 <div
 className="bg-white rounded-lg border border-[#e5e7eb] w-full max-w-sm shadow-[0_8px_24px_rgba(0,0,0,0.12)] animate-[fadeIn_0.2s_ease-out]"
 role="dialog"
 aria-modal="true"
 onClick={(e) => e.stopPropagation()}
 onKeyDown={(e) => { if (e.key === 'Escape' && !bulkLoading) setShowBulkConfirm(false); }}
 >
 <div className="p-6 text-center">
 <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mx-auto mb-4">
 <HiOutlineExclamation className="text-2xl text-red-600" />
 </div>
 <h3 className="m-0 text-lg font-semibold text-[#24292f]">Confirm Bulk Delete</h3>
 <p className="m-0 mt-2 text-sm text-[#57606a] leading-relaxed">
 You are about to delete{" "}
 <span className="font-bold text-[#24292f]">{selectedIds.length}</span>{" "}
 school{selectedIds.length !== 1 ? "s" : ""}. This action cannot be undone.
 </p>
 </div>
 <div className="flex gap-3 px-6 py-4 border-t border-[#e5e7eb] bg-[#f6f8fa] rounded-b-lg">
 <button
 className="flex-1 py-2.5 px-5 text-sm font-semibold rounded-md cursor-pointer transition-all duration-200 bg-white text-[#57606a] border border-[#d0d5dd] hover:bg-[#f3f4f6] disabled:opacity-50 disabled:cursor-not-allowed"
 onClick={() => setShowBulkConfirm(false)}
 disabled={bulkLoading}
 >
 Cancel
 </button>
 <button
 className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 px-5 text-sm font-semibold border-none rounded-md cursor-pointer transition-all duration-200 bg-red-600 text-white hover:bg-red-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
 onClick={handleBulkDelete}
 disabled={bulkLoading}
 >
 {bulkLoading ? (
 <>
 <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
 <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
 <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
 </svg>
 Deleting…
 </>
 ) : (
 "Confirm Delete"
 )}
 </button>
 </div>
 </div>
 </div>
 )}

 {/* Bulk Upload Modal */}
 {showUploadModal && (
 <div
 className="fixed inset-0 bg-black/40 flex items-center justify-center z-[200] p-4"
 onClick={() => !uploadLoading && setShowUploadModal(false)}
 >
 <div
 className="bg-white rounded-lg border border-[#e5e7eb] w-full max-w-md shadow-[0_8px_24px_rgba(0,0,0,0.12)] animate-[fadeIn_0.2s_ease-out]"
 role="dialog"
 aria-modal="true"
 onClick={(e) => e.stopPropagation()}
 onKeyDown={(e) => { if (e.key === 'Escape' && !uploadLoading) setShowUploadModal(false); }}
 >
 {/* Header */}
 <div className="flex justify-between items-center px-6 py-5 border-b border-[#e5e7eb]">
 <div>
 <h3 className="m-0 text-[15px] font-semibold text-[#24292f]">Bulk Upload Schools</h3>
 <p className="m-0 mt-1 text-[13px] text-[#8b949e]">Upload an Excel file (.xlsx) to import schools</p>
 </div>
 <button
 className="w-8 h-8 flex items-center justify-center rounded-md text-[#8b949e] hover:text-[#57606a] hover:bg-[#f3f4f6] transition-all duration-200 border-none bg-transparent cursor-pointer"
 onClick={() => setShowUploadModal(false)}
 disabled={uploadLoading}
 >
 <HiOutlineX className="text-lg" />
 </button>
 </div>

 {/* Body */}
 <div className="p-6 space-y-5">
 {/* Template Download */}
 <div className="flex items-center gap-3 p-3 bg-[#eff6ff] rounded-md border border-[#d0d5dd]">
 <HiOutlineDocumentText className="text-lg text-[#0078d4] flex-shrink-0" />
 <div className="flex-1">
 <p className="m-0 text-sm font-medium text-[#24292f]">Need a template?</p>
 <p className="m-0 text-xs text-[#57606a] mt-0.5">Required: name, udise_code, block_name. Optional: is_adopted, latitude, longitude</p>
 </div>
 <button
 className="inline-flex items-center gap-1 py-1.5 px-3 text-xs font-semibold rounded-lg cursor-pointer transition-all duration-200 bg-white text-[#0078d4] border border-[#d0d5dd] hover:bg-[#f3f4f6]"
 onClick={downloadTemplate}
 >
 <HiOutlineDownload className="text-sm" />
 Download
 </button>
 </div>

 {/* File Input */}
 <div>
 <label className="block mb-2 text-sm font-medium text-[#24292f]">Excel File (.xlsx)</label>
 <div
 className="relative border-2 border-dashed border-[#d0d5dd] rounded-md p-6 text-center hover:border-[#0078d4] transition-colors cursor-pointer"
 onClick={() => fileInputRef.current?.click()}
 >
 <input
 ref={fileInputRef}
 type="file"
 accept=".xlsx"
 className="hidden"
 onChange={handleFileChange}
 />
 <HiOutlineUpload className="text-2xl text-[#8b949e] mx-auto mb-2" />
 {uploadFile ? (
 <p className="m-0 text-sm font-medium text-[#24292f]">{uploadFile.name}</p>
 ) : (
 <p className="m-0 text-sm text-[#8b949e]">Click to select or drag a file</p>
 )}
 <p className="m-0 text-xs text-[#8b949e] mt-1">Max 5MB</p>
 </div>
 </div>

 {/* Upload Result */}
 {uploadResult && (
 <div className={`p-4 rounded-md border ${
 uploadResult.success
 ? "bg-[#f0fdf4] border-[#bbf7d0]"
 : "bg-red-50 border-red-200"
 }`}>
 <p className={`m-0 text-sm font-semibold ${
 uploadResult.success ? "text-[#16a34a]" : "text-red-700"
 }`}>
 {uploadResult.success
 ? ` Inserted ${uploadResult.inserted} record(s)`
 : uploadResult.message || "Validation failed"}
 </p>
 {uploadResult.errors && uploadResult.errors.length > 0 && (
 <div className="mt-3 max-h-40 overflow-y-auto space-y-1">
 {uploadResult.errors.map((err, i) => (
 <p key={i} className="m-0 text-xs text-red-600">
 Row {err.row}: {err.message}
 </p>
 ))}
 </div>
 )}
 </div>
 )}
 </div>

 {/* Footer */}
 <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#e5e7eb] bg-[#f6f8fa] rounded-b-lg">
 <button
 className="py-2.5 px-5 text-sm font-semibold rounded-md cursor-pointer transition-all duration-200 bg-white text-[#57606a] border border-[#d0d5dd] hover:bg-[#f3f4f6] disabled:opacity-50"
 onClick={() => setShowUploadModal(false)}
 disabled={uploadLoading}
 >
 {uploadResult?.success ? "Close" : "Cancel"}
 </button>
 {!uploadResult?.success && (
 <button
 className="inline-flex items-center justify-center gap-2 py-2.5 px-5 text-sm font-semibold border-none rounded-md cursor-pointer transition-all duration-200 bg-[#0078d4] text-white hover:bg-[#106ebe] shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
 onClick={handleUpload}
 disabled={!uploadFile || uploadLoading}
 >
 {uploadLoading ? (
 <>
 <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
 <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
 <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
 </svg>
 Uploading…
 </>
 ) : (
 <>
 <HiOutlineUpload className="text-base" />
 Upload
 </>
 )}
 </button>
 )}
 </div>
 </div>
 </div>
 )}
 </div>
 );
}

export default Schools;
